import dayjs from 'dayjs';
import { SPIRITUAL_SPHERES, type SpiritualSphere } from '@kitobxonlik/contracts';

/** Backend sana formati: `YYYY-MM-DD HH:mm:ss`, jarayon TZ'sida, offset yo'q (PLAN §2.8) */
export const API_DATETIME = 'YYYY-MM-DD HH:mm:ss';
export const API_DATE = 'YYYY-MM-DD';

export const fmtDate = (v: string | null | undefined) => (v ? dayjs(v).format('DD.MM.YYYY') : '—');
export const fmtDateTime = (v: string | null | undefined) =>
  v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—';
export const fmtTime = (v: string | null | undefined) => (v ? dayjs(v).format('HH:mm') : '—');
export const toApiDateTime = (d: Date | dayjs.Dayjs | number) => dayjs(d).format(API_DATETIME);
export const toApiDate = (d: Date | dayjs.Dayjs | number) => dayjs(d).format(API_DATE);

/** 30 240 ko'rinishida (guruhlar orasida bo'linmas bo'shliq) — DS tabular-nums bilan */
export const fmtNumber = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : new Intl.NumberFormat('uz-UZ').format(n).replace(/[\s,\u00a0\u202f]/g, '\u00a0');

export const fmtPercent = (n: number | null | undefined, digits = 0) =>
  n === null || n === undefined ? '—' : `${n.toFixed(digits)}%`;

/** mm:ss taymer */
export function fmtCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** Muqova paletkasi (.cv-1 … .cv-12) — id bo'yicha barqaror */
export const coverPalette = (id: number) => (Math.abs(id) % 12) + 1;

export const isSphere = (v: string): v is SpiritualSphere =>
  (SPIRITUAL_SPHERES as readonly string[]).includes(v);

/** Ma'naviy soha rangi — tokens.css `--sphere-*` */
export const sphereColorVar = (sphere: string) => `var(--sphere-${sphere})`;

/**
 * Enum qiymati (`legal-disciplinary`) -> javob kaliti (`legal_disciplinary`).
 *
 * Eski Go API enum'da defis, JSON maydon nomida pastki chiziq ishlatadi
 * (`SpiritualIndexResponse`, `BookReadStatsResponse`). 1:1 port qoidasi
 * bo'yicha o'zgartirilmadi — moslashtirish shu yerda, bitta joyda.
 */
export type SphereKey =
  | 'ethical'
  | 'professional'
  | 'legal_disciplinary'
  | 'spiritual_educational'
  | 'physical';

export const sphereKey = (sphere: string) => sphere.replace(/-/g, '_') as SphereKey;

/**
 * Tadbir rangi (`event.color_code`) inline style'ga tushadi — qiymat esa
 * boshqa foydalanuvchi (yoki eski Go API) yozgan ixtiyoriy satr. `url(...)`
 * yoki `image-set(...)` yozilsa brauzer TASHQI so'rov yuboradi (referer bilan),
 * shuning uchun faqat hex rang o'tkaziladi.
 */
const HEX_COLOR = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

export const safeColor = (v: string | null | undefined, fallback = 'var(--brand-500)') =>
  v && HEX_COLOR.test(v.trim()) ? v.trim() : fallback;

/**
 * Tashkilot: HR bergan nomi, nom bo'lmasa `#id`. Sarlavhasi «Tashkilot»
 * bo'lgan ustun/maydonlar uchun; yolg'iz turgan joyda `common.orgNumber`.
 */
export function orgLabel(name: string | null | undefined, id: number): string {
  return name?.trim() || `#${id}`;
}

/**
 * Uzun tashkilot nomi (`NEllipsis :line-clamp`) kesilganda chiqadigan tooltip:
 * mavzuga mos Naive tooltip, kengligi sidebar'dan oshmaydi — nom bir uzun
 * qatorga cho'zilmay, o'qiladigan bo'lib o'raladi.
 */
export const ORG_TOOLTIP = {
  placement: 'top-start',
  style: { maxWidth: 'var(--sidebar-w)' },
} as const;

/** "Ism Familiya" → "IF" */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}
